import { ReactComponent as AddIcon } from "assets/images/icons/btn_add.svg";
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as CheckDefIcon } from "assets/images/icons/checkbox_default.svg";
import { ReactComponent as CheckClearIcon } from "assets/images/icons/checkbox_clear.svg";
import { ReactComponent as CheckFillIcon } from "assets/images/icons/checkbox_fiilled.svg";
import { ReactComponent as BinIcon } from "assets/images/icons/icn_btn_bin.svg";

import React, { useRef, useState } from "react";
import zIndex from "@mui/material/styles/zIndex";
import {
  Box,
  Button,
  Typography,
  TextField,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import IpAddressTableRow from "./TableRow";
import AddIpAddressModal from "./AddIpAddressModal";
import UploadView from "./UploadView";
import FromClientView from "./FromClientView";
import Loading from "../loading";
import { MAX_IP_ADDRESS_ENTRIES } from "../util/const";

const MainView = ({
  getViewTitle,
  form,
  formValue,
  readyToRenderTable,
  closeModal,
  handleMainView,
  isEditMode,
  showEditGeneral = true,
  auth,
}) => {
  const theme = useTheme();
  const viewRef = useRef(null);
  const [isDirtyName, setIsDirtyName] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openUploadView, setOpenUploadView] = useState(false);
  const [openFromClientView, setOpenFromClientView] = useState(false);
  const [selectedIndexes, setSelectedIndexes] = useState([]);

  if (!readyToRenderTable) {
    return (
      <>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              paddingLeft: "20px",
            }}
          >
            {getViewTitle()}
          </Typography>
        </Box>
        <Loading />
      </>
    );
  }

  const isNameValid = isDirtyName
    ? formValue.name.trim().length > 0
    : true;
  const isIpAddressLimitExceeded = formValue.ipAddresses.length > MAX_IP_ADDRESS_ENTRIES;

  const viewWidth = viewRef && viewRef.current ? viewRef.current.offsetWidth : 0;
  let viewHeight = viewRef && viewRef.current ? viewRef.current.offsetHeight : 0;
  let marginTop = 0;
  if (viewRef && viewRef.current) {
    viewHeight = viewRef.current.offsetHeight;
    marginTop = viewHeight / 6;
  }

  return (
    <Box ref={viewRef} style={{ position: "relative", padding: 0 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
            paddingLeft: "20px",
          }}
        >
          {getViewTitle()}
        </Typography>
        <Button
          onClick={closeModal}
          sx={{
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: theme.palette.primary.transparent,
            },
          }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <Box sx={{ paddingLeft: "26px" }}>
        <Box sx={form.getRowStyle()}>
          <Box sx={form.getLabelStyle()}>IP Group Name</Box>
          <TextField
            inputProps={{ minLength: 1 }}
            sx={form.getTextfieldStyle(isNameValid)}
            value={formValue.name}
            onChange={(e) => {
              setIsDirtyName(true);
              handleMainView.changeFormValue("name", e.target.value);
            }}
          />
        </Box>

        <Box sx={form.getRowStyle()}>
          <Box sx={{ ...form.getLabelStyle(), width: "auto", flexShrink: 0, whiteSpace: "nowrap" }}>
            IP Address List
            <Box
              component="span"
              sx={{
                color: isIpAddressLimitExceeded
                  ? theme.palette.primary.redPrimary
                  : "inherit",
              }}
            >
              {` (Up to ${MAX_IP_ADDRESS_ENTRIES} entries)`}
            </Box>
          </Box>
          <Box display="flex" gap="10px" justifyContent="flex-end" sx={{ flexGrow: 1 }}>
            <OutlineRectangleButton
              startIcon={<AddIcon />}
              height="32px"
              onClick={() => setOpenAddModal(true)}
            >
              Add
            </OutlineRectangleButton>
            <OutlineRectangleButton height="32px" onClick={() => setOpenUploadView(true)}>
              Upload
            </OutlineRectangleButton>
            <OutlineRectangleButton height="32px" onClick={() => setOpenFromClientView(true)}>
              Add from Client List
            </OutlineRectangleButton>
            {selectedIndexes.length > 0 && (
              <OutlineRectangleButton
                startIcon={<BinIcon />}
                height="32px"
                onClick={() => {
                  handleMainView.deleteSelectedIpAddresses(selectedIndexes);
                  setSelectedIndexes([]);
                }}
              >
                Delete<Box sx={form.getNumberStyle()}>{selectedIndexes.length}</Box>
              </OutlineRectangleButton>
            )}
          </Box>
        </Box>

        <Box sx={{ ...form.getTableStyle() }}>
          <TableContainer
            component={Paper}
            sx={{
              "&.MuiPaper-root": {
                boxShadow: "none",
                border: "none",
              },
            }}
            style={{ height: "300px", overflowY: "auto" }}
          >
            <Table
              sx={{
                borderCollapse: "collapse",
                "& td, & th": {
                  border: "none",
                },
                "& tbody tr:nth-of-type(odd)": {
                  backgroundColor: "rgb(246, 246, 246)",
                },
                "& tbody tr:nth-of-type(even)": {
                  backgroundColor: theme.palette.primary.white,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "48px" }}>
                    <Checkbox
                      icon={<CheckDefIcon style={{ width: "25px", height: "25px" }} />}
                      checkedIcon={<CheckFillIcon style={{ width: "25px", height: "25px" }} />}
                      indeterminateIcon={<CheckClearIcon style={{ width: "25px", height: "25px" }} />}
                      indeterminate={
                        selectedIndexes.length > 0 &&
                        selectedIndexes.length < formValue.ipAddresses.length
                      }
                      checked={
                        formValue.ipAddresses.length > 0 &&
                        selectedIndexes.length === formValue.ipAddresses.length
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIndexes(formValue.ipAddresses.map((_, index) => index));
                        } else {
                          setSelectedIndexes([]);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>IP Address</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description (Optional)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formValue.ipAddresses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ textAlign: "center", padding: "20px" }}>
                      No Entry in the table
                    </TableCell>
                  </TableRow>
                ) : (
                  formValue.ipAddresses.map((ip, index) => (
                    <IpAddressTableRow
                      key={index}
                      ip={ip}
                      form={form}
                      index={index}
                      isSelected={selectedIndexes.includes(index)}
                      onSelect={(i) =>
                        setSelectedIndexes((prev) =>
                          prev.includes(i)
                            ? prev.filter((idx) => idx !== i)
                            : [...prev, i]
                        )
                      }
                      onIpChange={handleMainView.changeIpAddressItemValue}
                      onDescChange={handleMainView.changeDescriptionItemValue}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      <Box sx={{ margin: "24px 0 16px auto", display: "flex", justifyContent: "center" }}>
        <OutlineButton
          onClick={closeModal}
          sx={{ marginRight: "20px" }}
          width="100px"
          height="32px"
        >
          Cancel
        </OutlineButton>
        <SolidButton
          onClick={handleMainView.handleSave}
          width="100px"
          height="32px"
          disabled={!handleMainView.checkFormValid() || (isEditMode && !showEditGeneral)}
        >
          {isEditMode ? "Apply" : "Create"}
        </SolidButton>
      </Box>

      {openAddModal && (
        <Box
          style={{
            ...form.getMaskStyle(),
            width: viewWidth + 40 + "px",
            height: viewHeight + 60 + "px",
            transform: "translateY(-20px) translateX(-20px)",
          }}
        />
      )}

      {openAddModal && (
        <Box
          style={{
            ...form.getInnerViewStyle(zIndex),
            width: viewWidth + 40 + "px",
            transform: `translate(-20px, ${marginTop}px)`,
          }}
        >
          <Box style={form.getInnerContainerStyle()}>
            <AddIpAddressModal
              open={openAddModal}
              onClose={() => setOpenAddModal(false)}
              onApply={handleMainView.addIpAddresses}
            />
          </Box>
        </Box>
      )}

      {openUploadView && (
        <Box
          style={{
            ...form.getMaskStyle(),
            width: viewWidth + 40 + "px",
            height: viewHeight + 60 + "px",
            transform: "translateY(-20px) translateX(-20px)",
          }}
        />
      )}

      {openUploadView && (
        <Box
          style={{
            ...form.getInnerViewStyle(zIndex),
            width: viewWidth + 40 + "px",
            transform: `translate(-20px, ${marginTop - 40}px)`,
          }}
        >
          <Box style={form.getInnerContainerStyle()}>
            <UploadView
              form={form}
              onClose={() => setOpenUploadView(false)}
              onImport={handleMainView.importIpAddresses}
            />
          </Box>
        </Box>
      )}

      {openFromClientView && (
        <Box
          style={{
            ...form.getMaskStyle(),
            width: viewWidth + 40 + "px",
            height: viewHeight + 60 + "px",
            transform: "translateY(-20px) translateX(-20px)",
          }}
        />
      )}

      {openFromClientView && (
        <Box
          style={{
            ...form.getInnerViewStyle(zIndex),
            width: viewWidth + 40 + "px",
            transform: `translate(-20px, ${marginTop}px)`,
          }}
        >
          <Box style={form.getInnerContainerStyle()}>
            <FromClientView
              auth={auth}
              form={form}
              onClose={() => setOpenFromClientView(false)}
              onApply={handleMainView.addIpAddresses}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(MainView);
